import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import { apiCall } from "../api/apiCall";
import generateToastContainer from "../utils/ToastContainer";
import { toast } from "react-toastify";
import "./PatchUser.css";
import TopHeader from "../components/Navbar/TopHeader";
import { useTranslation } from "../i18n/UseTranslation";
import ConfirmDialog from "../components/Navbar/ConfirmLogoutDialog";
// 1. Ajout des imports Firebase
import { getAuth, onAuthStateChanged } from "firebase/auth";

type RoleResponse = { uuid: string; name: string; company_uuid: string };

type UserResponse = {
  firebaseUid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  address?: string;
  iban?: string;
  gender?: "male" | "female" | "other" | "";
  birth_date?: string | null;
  role_uuid?: string;
  company_uuid?: string;
  role?: { uuid: string; name: string };
  company?: { uuid: string; name: string };
  profile_picture?: string;
};

function PatchUser() {
  const { t } = useTranslation();
  const { firebaseUid } = useParams();
  const navigate = useNavigate();

  // 2. Ajout de l'état isAuthReady
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [iban, setIban] = useState<string | null>(null);
  const [roleUuid, setRoleUuid] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "other" | "">("");
  const [birthDate, setBirthDate] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [companyName, setCompanyName] = useState("—");
  const [adminRoleUuid, setAdminRoleUuid] = useState("");
  const [clientRoleUuid, setClientRoleUuid] = useState("");

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 16);
  const maxDateString = maxDate.toISOString().split("T")[0]; 

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserAndRoles = async () => {
      if (!isAuthReady || !firebaseUid) return; 
      
      try {
        const user = await apiCall<UserResponse>(`/users/${firebaseUid}`, { method: "GET" });
        const resolvedCompanyUuid = user.company_uuid ?? user.company?.uuid ?? "";
        setEmail(user.email ?? "");
        setFirstName(user.first_name ?? "");
        setLastName(user.last_name ?? "");
        setPhoneNumber(user.phone_number ?? null);
        setAddress(user.address ?? null);
        setIban(user.iban ?? null);
        setGender(user.gender ?? "");
        setBirthDate(user.birth_date ?? "");
        setRoleUuid(user.role_uuid ?? user.role?.uuid ?? "");
        setProfilePicture(user.profile_picture ?? "");
        setCompanyName(user.company?.name ?? "—");

        if (resolvedCompanyUuid) {
          const [adminRole, clientRole] = await Promise.all([
            apiCall<RoleResponse>(`/roles/${resolvedCompanyUuid}/admin`, { method: "GET" })
              .catch(() => ({ uuid: "" })),
            apiCall<RoleResponse>(`/roles/${resolvedCompanyUuid}/client`, { method: "GET" })
              .catch(() => ({ uuid: "" })),
          ]);
          
          setAdminRoleUuid(adminRole.uuid || "");
          setClientRoleUuid(clientRole.uuid || "");
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.error || error?.message || t.users.cannotLoad);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserAndRoles();
  }, [firebaseUid, isAuthReady, t]); 

  const handleRoleChange = (value: string) => {
    if (value === "admin") setRoleUuid(adminRoleUuid);
    else if (value === "client") setRoleUuid(clientRoleUuid);
    else setRoleUuid("");
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = e.target.value.replace(/\D/g, ""); 
    setPhoneNumber(onlyNumbers);
  };

  const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedIban = e.target.value.replace(/\s+/g, "").toUpperCase();
    setIban(cleanedIban);
  };

  const selectedRole = 
    (roleUuid && roleUuid === adminRoleUuid) ? "admin" : 
    (roleUuid && roleUuid === clientRoleUuid) ? "client" : 
    "";

  const handleSaveClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUid) return;

    if (iban && iban.length > 0 && iban.length !== 21) {
      toast.error(t.users.ibanLengthError || "L'IBAN doit contenir exactement 21 caractères.");
      return;
    }

    setIsSaveDialogOpen(true);
  };

  const executeSave = async () => {
    setIsSaveDialogOpen(false); 
    try {
      setIsSaving(true);
      await apiCall(`/users/${firebaseUid}`, {
        method: "PATCH",
        body: JSON.stringify({ firebaseUid, email, first_name: firstName, last_name: lastName, phone_number: phoneNumber, address, iban: iban ?? null, gender: gender || null, birth_date: birthDate || null, role_uuid: roleUuid, profile_picture: profilePicture }),
      });
      toast.success(t.users.updated);
      navigate(`/users/${firebaseUid}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || t.users.updateError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!firebaseUid) return;
    setIsDeleteDialogOpen(true);
  };

  const executeDelete = async () => {
    setIsDeleteDialogOpen(false); 
    try {
      setIsDeleting(true);
      await apiCall(`/users/${firebaseUid}`, { method: "DELETE" });
      toast.success(t.users.deleted);
      navigate("/users");
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || t.users.deleteError);
    } finally {
      setIsDeleting(false);
    }
  };

  const initials = ((firstName[0] ?? "") + (lastName[0] ?? "")).toUpperCase() || "?";

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />
        <div className="patch-user-loading">{t.users.loading}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />

      <div className="patch-user-wrapper">
        <form className="patch-user-card" onSubmit={handleSaveClick}>

          <div className="patch-user-header">
            <div className="patch-user-header-left">
              {profilePicture ? (
                <img className="patch-user-avatar" src={profilePicture} alt={`${firstName} ${lastName}`} />
              ) : (
                <div className="patch-user-avatar-initials">{initials}</div>
              )}
              <div>
                <h2 className="patch-user-title">{firstName} {lastName}</h2>
                <p className="patch-user-subtitle">{email}</p>
              </div>
            </div>
            {selectedRole && <span className="patch-user-role-badge">{t.users[selectedRole as keyof typeof t.users] || selectedRole}</span>}
          </div>

          <div className="patch-user-grid">
            <div className="patch-user-item patch-user-item-wide">
              <label>{t.users.uid}</label>
              <input className="mono" value={firebaseUid ?? ""} disabled />
            </div>

            <div className="patch-user-item">
              <label>{t.users.firstName}</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>

            <div className="patch-user-item">
              <label>{t.users.lastName}</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>

            <div className="patch-user-item">
              <label>{t.users.email}</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div className="patch-user-item">
              <label>{t.users.phone}</label>
              <input 
                type="tel" 
                value={phoneNumber ?? ""} 
                onChange={handlePhoneChange} 
                placeholder="Ex: 0612345678"
              />
            </div>

            <div className="patch-user-item">
              <label>{t.users.role}</label>
              <select value={selectedRole} onChange={(e) => handleRoleChange(e.target.value)}>
                <option value="">{t.users.selectRole}</option>
                <option value="client">{t.users.client}</option>
                <option value="accountant">{t.users.accountant}</option>
                <option value="admin">{t.users.admin}</option>
              </select>
            </div>

            <div className="patch-user-item">
              <label>{t.users.company}</label>
              <input value={companyName} disabled />
            </div>

            <div className="patch-user-item">
              <label>{t.users.iban}</label>
              <input 
                className="mono" 
                value={iban ?? ""} 
                onChange={handleIbanChange} 
                maxLength={21} 
                placeholder="FR..."
              />
              {iban && iban.length > 0 && iban.length < 21 && (
                <span style={{color: 'red', fontSize: '0.8rem', marginTop: '4px', display: 'block'}}>
                  {t.users.ibanMissingChars?.replace("{count}", (21 - iban.length).toString()) || `Il manque ${21 - iban.length} caractère(s).`}
                </span>
              )}
            </div>

            <div className="patch-user-item">
              <label>{t.users.gender}</label>
              <select value={gender} onChange={(e) => setGender(e.target.value as any)}>
                <option value="">{t.users.selectGender}</option>
                <option value="male">{t.users.male}</option>
                <option value="female">{t.users.female}</option>
                <option value="other">{t.users.other}</option>
              </select>
            </div>

            <div className="patch-user-item">
              <label>{t.users.birthDate}</label>
              <input 
                type="date" 
                max={maxDateString} 
                value={birthDate ? birthDate.slice(0, 10) : ""} 
                onChange={(e) => setBirthDate(e.target.value)} 
              />
            </div>

            <div className="patch-user-item patch-user-item-wide">
              <label>{t.users.address}</label>
              <input value={address ?? ""} onChange={(e) => setAddress(e.target.value)} />
            </div>
          </div>

          <div className="patch-user-actions">
            <button type="button" className="patch-user-delete" onClick={handleDeleteClick} disabled={isSaving || isDeleting}>
              {isDeleting ? t.users.deleting : t.users.deleteBtn}
            </button>
            <div className="patch-user-actions-right">
              <button type="button" className="patch-user-cancel" onClick={() => navigate(`/users/${firebaseUid}`)}>
                {t.users.cancelBtn}
              </button>
              <button type="submit" className="patch-user-save" disabled={isSaving}>
                {isSaving ? t.users.saving : t.users.saveBtn}
              </button>
            </div>
          </div>

        </form>
      </div>

      <ConfirmDialog
        isOpen={isSaveDialogOpen}
        title={t.users?.saveConfirmTitle || "Confirmer l'enregistrement"}
        content={t.users?.saveConfirmContent || "Voulez-vous vraiment enregistrer ces modifications ?"}
        cancelText={t.common?.cancel || "Annuler"}
        confirmText={t.users?.saveBtn || "Enregistrer"}
        onCancel={() => setIsSaveDialogOpen(false)}
        onConfirm={executeSave}
        variant="primary" 
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={t.users?.deleteConfirmTitle || "Confirmer la suppression"}
        content={t.users?.deleteConfirm?.replace("{name}", `${firstName} ${lastName}`) || `Voulez-vous vraiment supprimer l'utilisateur ${firstName} ${lastName} ?`}
        cancelText={t.common?.cancel || "Annuler"}
        confirmText={t.users?.deleteBtn || "Supprimer"}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onConfirm={executeDelete}
        variant="danger"
      />

      {generateToastContainer()}
    </div>
  );
}

export default PatchUser;